import { OPTION_ALL, OPTION_BLANK } from '@src/suite/common/enums/constants';
import { FilterSavedState } from '@src/suite/common/store/inline-filter/inline-filter.models';
import { DateTime } from 'luxon';

import { PSTFieldNames } from '../data-models/fields/pst-field-names';
import { StoryOutputType } from '../enums/enums';

export type ValueKey = string;
export type Guid = string;

export interface RecordWithGuid {
    Guid?: string;
}

export type ReverseIndex = Map<string, Map<ValueKey, Set<Guid>>>;
export type FacetCounts = Record<string, Record<ValueKey, number>>;

export type CompiledPredicate = (record: Record<string, unknown>) => boolean;
export type CompiledSort<T> = (records: readonly T[]) => T[];

export const TODAY_ONLY_FILTER_ID = '__TodayOnly__';
export const SORT_BY_FILTER_ID = '__SortBy__';

export const mergeFilters = (config: readonly StorytellingFilterDefinition[], state: FilterSavedState | null | undefined): StorytellingFilterDefinition[] =>
    config.map(def => ({ ...def, Value: state?.[def.ID] }));

const isLookupValue = (v: unknown): v is LookupValue => !!v && typeof v === 'object' && typeof (v as LookupValue).id === 'string';

export const extractKeys = (record: Record<string, any>, filterDef: StorytellingFilterDefinition): ValueKey[] => {
    const field = filterDef.PSTField ?? filterDef.ID;
    if (!field) return [];
    const raw = record?.[field];

    if (raw === undefined || raw === null || String(raw ?? '').trim() === '' || (Array.isArray(raw) && raw.length === 0)) {
        return [OPTION_BLANK];
    }

    switch (filterDef.Type) {
        case 'combobox':
        case 'select':
            if (isLookupValue(raw)) {
                if (field === PSTFieldNames.PSTStoryOutputType && raw.id === StoryOutputType.GENERIC) {
                    return [OPTION_BLANK];
                } else {
                    return raw.id === '' ? [OPTION_BLANK] : [raw.id];
                }
            }
            if (typeof raw === 'string') return raw.trim() === '' ? [OPTION_BLANK] : [raw];
            return [OPTION_BLANK];
        case 'checklist':
        case 'lookuplist':
            if (Array.isArray(raw)) {
                const keys = raw.map(v => (isLookupValue(v) ? v.id : typeof v === 'string' ? v : null)).filter((k): k is string => !!k);
                return keys.length ? keys : [OPTION_BLANK];
            }
            if (isLookupValue(raw)) return raw.id === '' ? [OPTION_BLANK] : [raw.id];
            return [OPTION_BLANK];
        case 'staffidlist':
            if (Array.isArray(raw)) {
                const keys = raw.filter((v): v is string => typeof v === 'string');
                return keys.length ? keys : [OPTION_BLANK];
            }
            return [OPTION_BLANK];
        case 'checkbox':
            if (filterDef.ID === TODAY_ONLY_FILTER_ID || filterDef.ID === SORT_BY_FILTER_ID) return [];
            return [raw ? 'true' : 'false'];
        default:
            return [];
    }
};

export const buildReverseIndex = <T extends RecordWithGuid>(records: readonly T[], filterDefs: readonly StorytellingFilterDefinition[]): ReverseIndex => {
    const index: ReverseIndex = new Map();
    for (const def of filterDefs) {
        const fieldId = def.ID;
        if (!fieldId) continue;
        const keyMap = new Map<ValueKey, Set<Guid>>();
        index.set(fieldId, keyMap);
        for (const record of records) {
            const guid = record.Guid;
            if (!guid) continue;
            const keys = extractKeys(record as Record<string, any>, def);
            for (const key of keys) {
                let set = keyMap.get(key);
                if (!set) {
                    set = new Set();
                    keyMap.set(key, set);
                }
                set.add(guid);
            }
        }
    }
    return index;
};

const selectedKeysForFilter = (filter: StorytellingFilterDefinition): ValueKey[] | null => {
    const v = filter.Value;
    if (v === undefined || v === null || v === OPTION_ALL) return null;

    switch (filter.Type) {
        case 'combobox':
        case 'select':
        case 'checklist':
        case 'lookuplist':
            if (Array.isArray(v)) {
                const keys = v.map((item: unknown) => (isLookupValue(item) ? item.id : typeof item === 'string' ? item : null)).filter((k): k is string => !!k && k !== OPTION_ALL);
                return keys.length ? keys : null;
            }
            if (isLookupValue(v)) return v.id === OPTION_ALL ? null : [v.id];
            if (typeof v === 'string') return v === OPTION_ALL ? null : [v];
            return null;
        case 'staffidlist':
            if (Array.isArray(v)) {
                const keys = (v as any[]).filter((item): item is string => typeof item === 'string' && item !== OPTION_ALL);
                return keys.length ? keys : null;
            }
            return null;
        case 'checkbox':
            if (v === OPTION_ALL) return null;
            if (v === true || v === 'YES' || v === '@yes') return ['true'];
            if (v === false || v === 'NO' || v === '@no') return ['false'];
            if (v === OPTION_BLANK || v === '@blank') return [OPTION_BLANK];
            return null;
        default:
            return null;
    }
};

const toDate = (v: unknown): DateTime | null => (v instanceof DateTime && v.isValid ? v : null);

const isPredicateFilter = (filter: StorytellingFilterDefinition): boolean => filter.ID === TODAY_ONLY_FILTER_ID;

const isSortFilter = (filter: StorytellingFilterDefinition): boolean => filter.ID === SORT_BY_FILTER_ID;

const compileSort = <T>(filter: StorytellingFilterDefinition): CompiledSort<T> | null => {
    const dir = filter.Value;
    if (dir !== 'asc' && dir !== 'desc') return null;
    const field = filter.PSTField ?? 'PSTTargetDate';
    const sign = dir === 'asc' ? 1 : -1;
    return records => {
        const copy = [...records];
        copy.sort((a, b) => {
            const da = toDate((a as Record<string, unknown>)[field]);
            const db = toDate((b as Record<string, unknown>)[field]);
            if (!da && !db) return 0;
            if (!da) return 1;
            if (!db) return -1;
            if (da < db) return -sign;
            if (da > db) return sign;
            return 0;
        });
        return copy;
    };
};

const compilePredicate = (filter: StorytellingFilterDefinition): CompiledPredicate | null => {
    const field = filter.PSTField ?? filter.ID;
    if (!field) return null;

    if (filter.ID === TODAY_ONLY_FILTER_ID) {
        const v = filter.Value;
        const active = v && v !== OPTION_ALL && !(Array.isArray(v) && v.every((item: { id?: string } | string) => (typeof item === 'object' ? item?.id === OPTION_ALL : item === OPTION_ALL)));
        if (!active) return null;
        const todayISO = DateTime.now().toISODate();
        return record => toDate(record[field])?.toISODate() === todayISO;
    }

    return null;
};

const intersect = <T>(a: Set<T>, b: Set<T>): Set<T> => {
    const out = new Set<T>();
    const [small, large] = a.size <= b.size ? [a, b] : [b, a];
    for (const v of small) {
        if (large.has(v)) out.add(v);
    }
    return out;
};

export const applyFilter = <T extends RecordWithGuid>(records: readonly T[], filters: readonly StorytellingFilterDefinition[], index: ReverseIndex): T[] => {
    if (!records.length || !filters.length) return [...records];

    const keyFilters: StorytellingFilterDefinition[] = [];
    const predicateFilters: StorytellingFilterDefinition[] = [];
    let sortFilter: StorytellingFilterDefinition | null = null;
    for (const f of filters) {
        if (!f || f.NotUsed) continue;
        if (isSortFilter(f)) {
            sortFilter = f;
            continue;
        }
        if (isPredicateFilter(f)) {
            if (f.Value !== undefined && f.Value !== null && f.Value !== '') predicateFilters.push(f);
        } else {
            const keys = selectedKeysForFilter(f);
            if (keys) keyFilters.push(f);
        }
    }

    const sortFn = sortFilter ? compileSort<T>(sortFilter) : null;

    if (!keyFilters.length && !predicateFilters.length) {
        return sortFn ? sortFn(records) : [...records];
    }

    let candidateGuids: Set<Guid> | null = null;
    for (const f of keyFilters) {
        const keys = selectedKeysForFilter(f);
        if (!keys) continue;
        const keyMap = index.get(f.ID);
        if (!keyMap) {
            return [];
        }
        const unionForField = new Set<Guid>();
        for (const key of keys) {
            const guids = keyMap.get(key);
            if (guids) {
                for (const g of guids) unionForField.add(g);
            }
        }
        if (unionForField.size === 0) return [];
        candidateGuids = candidateGuids ? intersect(candidateGuids, unionForField) : unionForField;
        if (candidateGuids.size === 0) return [];
    }

    const narrowed = candidateGuids ? records.filter(r => r.Guid && candidateGuids!.has(r.Guid)) : [...records];

    let predicateFiltered: T[];
    if (predicateFilters.length) {
        const compiled = predicateFilters.map(compilePredicate).filter((p): p is CompiledPredicate => !!p);
        predicateFiltered = compiled.length ? narrowed.filter(r => compiled.every(p => p(r as Record<string, unknown>))) : narrowed;
    } else {
        predicateFiltered = narrowed;
    }

    return sortFn ? sortFn(predicateFiltered) : predicateFiltered;
};

export const computeFacetCounts = <T extends RecordWithGuid>(
    records: readonly T[],
    filterDefs: readonly StorytellingFilterDefinition[],
    index: ReverseIndex,
    activeFilters: readonly StorytellingFilterDefinition[]
): FacetCounts => {
    const counts: FacetCounts = {};
    for (const def of filterDefs) {
        const fieldId = def.ID;
        if (!fieldId) continue;
        const othersActive = activeFilters.filter(f => f.ID !== fieldId);
        const narrowed = applyFilter(records, othersActive, index);
        const keyMap = index.get(fieldId);
        if (!keyMap) {
            counts[fieldId] = {};
            continue;
        }
        const fieldCounts: Record<ValueKey, number> = {};
        const narrowedGuids = new Set(narrowed.map(r => r.Guid!).filter(Boolean));
        for (const [key, guidSet] of keyMap) {
            let c = 0;
            for (const g of guidSet) {
                if (narrowedGuids.has(g)) c++;
            }
            if (c > 0) fieldCounts[key] = c;
        }
        counts[fieldId] = fieldCounts;
    }
    return counts;
};
